import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, LinearProgress, Switch, FormControlLabel, Alert } from '@mui/material';
import { styled, keyframes } from '@mui/system';

// Quantum field animation
const quantumPulse = keyframes`
  0% { 
    transform: scale(1) rotate(0deg);
    opacity: 0.6;
    filter: hue-rotate(0deg);
  }
  25% { 
    transform: scale(1.1) rotate(90deg);
    opacity: 0.8;
    filter: hue-rotate(90deg);
  }
  50% { 
    transform: scale(1.2) rotate(180deg);
    opacity: 1;
    filter: hue-rotate(180deg);
  }
  75% { 
    transform: scale(1.1) rotate(270deg);
    opacity: 0.8;
    filter: hue-rotate(270deg);
  }
  100% { 
    transform: scale(1) rotate(360deg);
    opacity: 0.6;
    filter: hue-rotate(360deg);
  }
`;

const particleFlow = keyframes`
  0% {
    transform: translateX(-50px);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(400px);
    opacity: 0;
  }
`;

const QuantumField = styled(Box)`
  position: relative;
  height: 400px;
  background: linear-gradient(45deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  border: 2px solid #00ff41;
  border-radius: 12px;
  overflow: hidden;
  margin: 20px 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(0, 255, 65, 0.1) 0%, transparent 70%);
    animation: ${quantumPulse} 4s ease-in-out infinite;
  }
`;

const Particle = styled(Box)<{ delay: number; color: string }>`
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.color};
  box-shadow: 0 0 10px ${props => props.color};
  animation: ${particleFlow} 3s linear infinite;
  animation-delay: ${props => props.delay}s;
  top: ${props => Math.random() * 380}px;
`;

const SlitBox = styled(Box)`
  position: absolute;
  left: 50%;
  top: 10%;
  transform: translateX(-50%);
  width: 20px;
  height: 80%;
  background: linear-gradient(to bottom, #333 40%, transparent 45%, transparent 55%, #333 60%);
  border: 1px solid #00ff41;
`;

const DetectorScreen = styled(Box)`
  position: absolute;
  right: 20px;
  top: 10%;
  width: 40px;
  height: 80%;
  background: linear-gradient(to right, transparent, rgba(255, 0, 255, 0.3));
  border: 1px solid #ff00ff;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 10px 5px;
`;

const InterferencePattern = styled(Box)<{ intensity: number }>`
  width: 100%;
  height: 8px;
  background: linear-gradient(to right, 
    transparent, 
    rgba(0, 255, 65, ${props => props.intensity}), 
    transparent
  );
  margin: 2px 0;
`;

interface QuantumExperimentProps {
  onDataPoint: (data: { timestamp: number; pattern: 'wave' | 'particle'; bit: number }) => void;
}

export const QuantumExperiment: React.FC<QuantumExperimentProps> = ({ onDataPoint }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [delayedChoice, setDelayedChoice] = useState<'wave' | 'particle'>('wave');
  const [photonCount, setPhotonCount] = useState(0);
  const [detectionPattern, setDetectionPattern] = useState<number[]>([]);
  const [retrocausalityDetected, setRetrocausalityDetected] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        // Simulate photon detection
        const newPhotonCount = photonCount + 1;
        setPhotonCount(newPhotonCount);

        // Generate detection pattern based on delayed choice
        const pattern = generateDetectionPattern(delayedChoice);
        setDetectionPattern(pattern);

        // Check for retrocausality (theoretical)
        const bit = delayedChoice === 'wave' ? 1 : 0;
        onDataPoint({
          timestamp: Date.now(),
          pattern: delayedChoice,
          bit
        });

        // Simulate retrocausality detection (for demo purposes)
        if (newPhotonCount > 100 && Math.random() > 0.95) {
          setRetrocausalityDetected(true);
          setTimeout(() => setRetrocausalityDetected(false), 3000);
        }
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, delayedChoice, photonCount, onDataPoint]);

  const generateDetectionPattern = (choice: 'wave' | 'particle'): number[] => {
    if (choice === 'wave') {
      // Interference pattern - periodic intensities
      return Array.from({ length: 20 }, (_, i) => 
        Math.abs(Math.sin((i * Math.PI) / 4)) * (0.5 + Math.random() * 0.5)
      );
    } else {
      // Particle pattern - random distribution
      return Array.from({ length: 20 }, () => Math.random() * 0.8);
    }
  };

  const handleStart = () => {
    setIsRunning(true);
    setPhotonCount(0);
    setDetectionPattern([]);
  };

  const handleStop = () => {
    setIsRunning(false);
  };

  const handleDelayedChoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDelayedChoice(event.target.checked ? 'wave' : 'particle');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#00ff41', textAlign: 'center' }}>
        CRONOS Quantum Time Communication Experiment
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', border: '1px solid #00ff41' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#00ff41', mb: 2 }}>
                Delayed-Choice Quantum Eraser
              </Typography>

              <QuantumField>
                {/* Photon particles */}
                {isRunning && Array.from({ length: 8 }).map((_, i) => (
                  <Particle
                    key={i}
                    delay={i * 0.3}
                    color={i % 2 === 0 ? '#00ff41' : '#ff00ff'}
                  />
                ))}

                {/* Double slit */}
                <SlitBox />

                {/* Detector screen with interference pattern */}
                <DetectorScreen>
                  {detectionPattern.map((intensity, i) => (
                    <InterferencePattern key={i} intensity={intensity} />
                  ))}
                </DetectorScreen>

                {/* Quantum field overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 30% 70%, rgba(0, 255, 65, 0.05), transparent)',
                    pointerEvents: 'none'
                  }}
                />
              </QuantumField>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={isRunning ? handleStop : handleStart}
                  sx={{
                    background: isRunning ? 'linear-gradient(45deg, #ff0040, #ff4000)' : 'linear-gradient(45deg, #00ff41, #00a82d)',
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                >
                  {isRunning ? 'STOP EXPERIMENT' : 'START EXPERIMENT'}
                </Button>

                <FormControlLabel
                  control={
                    <Switch
                      checked={delayedChoice === 'wave'}
                      onChange={handleDelayedChoiceChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#00ff41',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#00ff41',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: '#fff' }}>
                      Delayed Choice: {delayedChoice === 'wave' ? 'WAVE (Interference)' : 'PARTICLE (Which-Path)'}
                    </Typography>
                  }
                />
              </Box>

              <Typography variant="body2" sx={{ color: '#aaa', mt: 2 }}>
                Photons detected: {photonCount.toLocaleString()}
              </Typography>

              {isRunning && (
                <LinearProgress
                  sx={{
                    mt: 1,
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #00ff41, #ff00ff)'
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', border: '1px solid #ff00ff', mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ff00ff', mb: 2 }}>
                Retrocausality Monitor
              </Typography>

              <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                Current Pattern: {delayedChoice === 'wave' ? 'Interference' : 'Particle'}
              </Typography>

              <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                Encoded Bit: {delayedChoice === 'wave' ? '1' : '0'}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#00ff41' }}>
                  Theory: Future measurement choice affects past detection pattern
                </Typography>
              </Box>

              {retrocausalityDetected && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mt: 2, 
                    background: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid #ff9800',
                    '& .MuiAlert-message': { color: '#ff9800' }
                  }}
                >
                  Anomalous correlation detected! Investigating...
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', border: '1px solid #00ffff' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#00ffff', mb: 2 }}>
                Experimental Notes
              </Typography>

              <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                • Standard QM: No signalling to past
              </Typography>
              
              <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                • Observer effect: Future choice correlates with past
              </Typography>
              
              <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                • Multiverse theory: Branch messaging possible
              </Typography>
              
              <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                • 1-bit time modem: Theoretical breakthrough
              </Typography>

              <Typography variant="body2" sx={{ color: '#00ff41', mt: 2, fontStyle: 'italic' }}>
                "If we could break the no-communication theorem, even 1 bit backwards in time would change everything."
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};